import {
  CAMPAIGN_BY_ID,
  CAMPAIGN_QUEUED_CONTACTS,
  CAMPAIGNS,
  GET_CAMPAIGN_SCHEDULER_STATS,
  GET_CAMPAIGN_STATS,
  GET_CAMPAIGN_TRACKING,
  POST_CAMPAIGN_TEST_SCHEDULER,
  SEND_CAMPAIGNS,
  UPDATE_CAMPAIGN,
  CAMPAIGN_SEARCH_TOKEN_BY_ID,
  CREATE_RELATED_CAMPAIGN,
  REBALANCE_ROUND_ROBIN,
} from '@mm/api-routes';
import { CampaignType } from '../model/CampaignsModel';
import AuthenticatedController from './AuthenticatedController';
import { DEFAULT_HEADERS } from './Controller';

export type AdditionalRetrieveQueryParams = Partial<
  Pick<CampaignType, 'name' | 'status' | 'active'>
> & {
  status?: CampaignType['status'][];
};

export type CampaignsRetrievalParams = {
  botId: number;
  pageNumber: number;
  pageSize: number;
  additionalParams?: AdditionalRetrieveQueryParams;
  dripCampaign?: boolean;
  withSchedulersAttributes?: boolean;
  signal?: AbortSignal | null;
};

export type CampaignResourcePayload = {
  success: boolean;
  campaign: CampaignType;
  campaigns?: CampaignType[];
  errors: any[];
};

export default class CampaignsController extends AuthenticatedController {
  static getAll(params: CampaignsRetrievalParams) {
    const {
      botId,
      pageNumber,
      pageSize,
      additionalParams = {},
      dripCampaign,
      withSchedulersAttributes,
      signal = undefined,
    } = params;
    return super.get(
      CAMPAIGNS(
        botId,
        pageNumber,
        pageSize,
        additionalParams,
        dripCampaign,
        withSchedulersAttributes,
      ),
      null,
      DEFAULT_HEADERS,
      signal,
    );
  }

  static getQueuedContacts(
    id: number,
    schedulerId: number,
    page: number,
    perPage: number,
  ) {
    return super.get(CAMPAIGN_QUEUED_CONTACTS(id, schedulerId, page, perPage));
  }

  static getById(id: number) {
    return super.get(CAMPAIGN_BY_ID(id));
  }

  static async postCampaign(
    entity: CampaignType,
  ): Promise<CampaignResourcePayload> {
    return super.post(SEND_CAMPAIGNS, entity);
  }

  static async clone(
    campaignId: number | string,
    facebookPageIds?: number[],
    botIds?: number[],
  ): Promise<CampaignResourcePayload> {
    return super.post(CAMPAIGN_BY_ID(campaignId) + '/clone', {
      withRelatedObjects: true,
      facebookPageIds,
      botIds,
    });
  }

  static async getSearchToken(campaignId: number): Promise<CampaignType> {
    return super.get(CAMPAIGN_SEARCH_TOKEN_BY_ID(campaignId));
  }

  static async postPUDripCampaign(botId: number) {
    return super.post(SEND_CAMPAIGNS, { src: 'product_updates', botId });
  }

  static async putCampaign(
    entity: CampaignType,
    campaignId: number,
  ): Promise<CampaignResourcePayload> {
    return super.put(UPDATE_CAMPAIGN(campaignId), entity);
  }

  static getTracking(
    campaignId: number,
    pageNumber: number,
    pageSize: number,
    query?: string,
  ) {
    return super.get(GET_CAMPAIGN_TRACKING(campaignId, pageNumber, pageSize));
  }

  static getCampaignStats(campaignId: number) {
    return super.get(GET_CAMPAIGN_STATS(campaignId));
  }

  static getCampaignSchedulerStats(
    campaignId: number,
    schedulerId: number,
    startDate?: string,
    endDate?: string,
  ) {
    return super.get(
      GET_CAMPAIGN_SCHEDULER_STATS(campaignId, schedulerId, startDate, endDate),
    );
  }

  static async deleteCampaign(campaignId) {
    return super.delete(UPDATE_CAMPAIGN(campaignId));
  }

  static async createRelatedCampaign(
    campaignId: number,
    relatedCampaignType: 'unicorn_detector',
  ): Promise<{ success?: boolean; campaign?: CampaignType[] }> {
    return super.post(CREATE_RELATED_CAMPAIGN(campaignId, relatedCampaignType));
  }

  static async postCampaignTestScheduler(
    campaignId: number,
    schedulerId: number,
    testNumbers: string[],
  ) {
    return super.post(POST_CAMPAIGN_TEST_SCHEDULER(campaignId, schedulerId), {
      testNumbers,
    });
  }

  static async rebalanceRoundRobin(campaignId: number) {
    return super.post(REBALANCE_ROUND_ROBIN(campaignId));
  }
}
